import type { FC } from 'react';

import { ProductSwitchCurrentProductResponse, ProductSwitchProductResponse } from '@eneco/dc-be/client';
import { Bucket, Stack, Stretch, TextLink } from '@eneco/sparky';
import { styled } from '@eneco/sparky/stitches';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { ProductSwitchRenderingExtended } from '@sitecore/types/manual/be/ProductSwitchExtended';

import ReadFormField from '../../../components/ReadFormField';
import { getSitecoreProduct } from '../../../utils/getSitecoreProduct';

type SummaryCardProps = {
  fieldGroup:
    | ProductSwitchRenderingExtended['fields']['summaryElectricityCard']
    | ProductSwitchRenderingExtended['fields']['summaryGasCard'];
  originalProduct: ProductSwitchCurrentProductResponse;
  newProduct: ProductSwitchProductResponse;
  isFrench: boolean;
  onEdit?: (ean: string) => void;
  hasOrderId: boolean;
};

const SummaryCard: FC<SummaryCardProps> = ({
  fieldGroup,
  originalProduct,
  newProduct,
  isFrench,
  onEdit,
  hasOrderId,
}) => {
  const { fields } = useContent<ProductSwitchRenderingExtended>();
  const sitecoreProduct = getSitecoreProduct({
    technicalProductName: newProduct.technicalProductName ?? '',
    enecoProductsList: fields.productSwitchProposal.enecoProductsList,
  });
  const { trackEventBE } = useTracking();

  return (
    <Stretch height={true}>
      <StretchedBucket title={fieldGroup.title.value}>
        <Bucket.Content>
          <Stack.Item grow>
            <Stack direction="column" gap={6}>
              <ReadFormField label={fieldGroup.eanLabel.value} value={originalProduct.ean} />
              <ReadFormField
                label={fieldGroup.productNameLabel.value}
                value={sitecoreProduct?.productDisplayName?.value ?? ''}
              />
              <ReadFormField
                label={fieldGroup.rateCardLabel.value}
                value={
                  <TextLink
                    target="_blank"
                    href={isFrench ? (newProduct.tariffChartUrlFr ?? '') : (newProduct.tariffChartUrlNl ?? '')}
                    onClick={() => {
                      trackEventBE('be_myeneco_tariffcard_click', {
                        status: 'click',
                        data: { technicalProductName: newProduct.technicalProductName, isSummary: true },
                      });
                    }}>
                    {fieldGroup.rateCardLinkLabel.value}
                  </TextLink>
                }
              />
            </Stack>
          </Stack.Item>
          {originalProduct.ean && !hasOrderId && (
            <>
              <Stack.Item grow />
              <Stack.Item shrink>
                <TextLink onClick={() => onEdit && onEdit(originalProduct.ean!)} emphasis="high">
                  {fieldGroup.editButtonLabel.value}
                </TextLink>
              </Stack.Item>
            </>
          )}
        </Bucket.Content>
      </StretchedBucket>
    </Stretch>
  );
};

export default SummaryCard;

// th-child(3) Needed to stretch the content in a Bucket.
const StretchedBucket = styled(Bucket, {
  height: '100% !important',
  '& > div:nth-child(3)': {
    height: '100% !important',
  },
  '& > div:nth-child(3) > div': {
    height: '100% !important',
  },
});
